import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, X, PawPrint, ToggleLeft, ToggleRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { getPets, getCustomerStats } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';

const Pets = () => {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ customer_count: 0, pet_count: 0 });
  const [unneuteredOnly, setUnneuteredOnly] = useState(false);

  useEffect(() => {
    getCustomerStats().then(res => setStats(res.data)).catch(() => {});
  }, []);

  const loadPets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPets(undefined, search || undefined);
      setPets(res.data);
    } catch (error) {
      console.error('Error loading pets:', error);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => { loadPets(); }, search ? 300 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPets, search]);

  const speciesLabel = (s) => ({ dog: 'Köpek', cat: 'Kedi', bird: 'Kuş', other: 'Diğer' }[s] || s);

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4" data-testid="pets-page">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Hastalar</h1>
            <span className="flex items-center gap-1 bg-[#FF9500]/10 text-[#FF9500] rounded-full px-2.5 py-0.5 text-xs font-bold" data-testid="pet-count-badge">
              <PawPrint size={13} /> {stats.pet_count}
            </span>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={18} />
          <Input
            type="search"
            placeholder="Hasta Ara (İsim veya Mikroçip No)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10 h-11 text-sm bg-[#F2F2F7] border-0 rounded-xl"
            data-testid="pets-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Uneutered Toggle */}
        <div className="flex items-center justify-between mt-3" data-testid="unneutered-filter">
          <span className="text-xs font-medium text-[#8E8E93] flex items-center gap-1.5">
            <PawPrint size={13} className="text-[#FF9500]" />
            Kısırlaştırılmamış hastalar
          </span>
          <button onClick={() => setUnneuteredOnly(!unneuteredOnly)} data-testid="unneutered-toggle">
            {unneuteredOnly
              ? <ToggleRight size={36} className="text-[#FF9500]" />
              : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
          </button>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : (() => {
          const filtered = unneuteredOnly
            ? pets.filter(p => (p.species === 'cat' || p.species === 'dog') && p.is_neutered !== true)
            : pets;
          return filtered.length > 0 ? (
          <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden">
            {filtered.map((pet) => (
              <button
                key={pet.id}
                onClick={() => navigate(`/customer/${pet.customer_id}`, { state: { petId: pet.id } })}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                data-testid={`pet-row-${pet.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <PetAvatar species={pet.species} gender={pet.gender} size={14} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{pet.name}</p>
                      <span className="text-[11px] text-[#8E8E93] bg-[#F2F2F7] px-1.5 py-0.5 rounded">{speciesLabel(pet.species)}{pet.breed ? ` - ${pet.breed}` : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-[#6b6de3] font-medium">{pet.customer_name}</span>
                      {pet.customer_phone && <span className="text-xs text-[#8E8E93]">{pet.customer_phone}</span>}
                      {pet.microchip_no && <span className="text-[10px] text-[#8E8E93]">Çip: {pet.microchip_no}</span>}
                    </div>
                  </div>
                </div>
                <ChevronRight size={20} className="text-[#C7C7CC] shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          <div className="ios-card text-center py-12">
            <PawPrint size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93]">{search ? 'Sonuç bulunamadı' : unneuteredOnly ? 'Kısırlaştırılmamış hasta yok' : 'Henüz hasta yok'}</p>
          </div>
        );
        })()}
      </main>

      <BottomNav />
    </div>
  );
};

export default Pets;
